// TODO: Custom add for different environments
import axios, { InternalAxiosRequestConfig } from "axios";
import { LOCAL_STORAGE } from "shared/constants";
import PATHS from "shared/constants/paths";
import { User } from "shared/types";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const BASE_URL = `${API_BASE_URL}/api`;

// Hold pending request so we can re-fire them
const requestQueue: any = [];
// Check if we are refreshing the token
let isRefreshing = false;

const client = axios.create({
  baseURL: BASE_URL,
});

client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Every tab of the admin shares one stored session, and the server rotates the
// refresh token on each use. Two tabs whose token expired at the same moment
// (a laptop waking up, say) used to both send the same refresh token: the
// server rotated it for one and refused the other, whose logout then cleared
// the session the first tab had just renewed. The Web Locks API makes the tabs
// refresh one at a time; without it (older browsers, or a page served over
// plain http on a LAN address) the refresh runs unguarded as before.
const withRefreshLock = <T>(run: () => Promise<T>): Promise<T> =>
  typeof navigator !== "undefined" && navigator.locks ? navigator.locks.request("refresh-token", run) : run();

/**
 * Returns an access token newer than the one the server refused
 * (`refusedAuth`, the refused request's Authorization header), refreshing the
 * session only when no other tab already has.
 */
const refreshSession = (refusedAuth: unknown, user_id: string): Promise<string> =>
  withRefreshLock(async () => {
    const stored = localStorage.getItem(LOCAL_STORAGE.TOKEN);
    if (stored && `Bearer ${stored}` !== refusedAuth) return stored;

    const refreshToken = localStorage.getItem(LOCAL_STORAGE.REFRESH_TOKEN);
    if (!refreshToken) throw new Error("No refresh token");
    const response = await client.post("/v1/auth/refresh", {
      user_id,
      refresh_token: refreshToken,
    });
    localStorage.setItem(LOCAL_STORAGE.TOKEN, response.data.data.token);
    localStorage.setItem(LOCAL_STORAGE.REFRESH_TOKEN, response.data.data.refresh_token);
    return response.data.data.token as string;
  });

client.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // A request that never got a response (the network dropped, the API is
    // unreachable, the request was cancelled) has no status to read; it is
    // that request's own failure, not a session problem.
    if (error.response?.status === 401) {
      const userObj = localStorage.getItem(LOCAL_STORAGE.USER);
      const refreshToken = localStorage.getItem(LOCAL_STORAGE.REFRESH_TOKEN);
      if (!userObj) {
        return Promise.reject(error);
      }

      const user: User = JSON.parse(userObj);
      const user_id = user.id;
      const originalRequest = error.config;
      // Check if there is a refresh token and this is the first time we are retrying
      if (refreshToken && !originalRequest._retry) {
        // Prevent infinite loop
        originalRequest._retry = true;
        if (!isRefreshing) {
          isRefreshing = true;
          let token: string;
          try {
            token = await refreshSession(originalRequest.headers["Authorization"], user_id);
          } catch {
            window.location.href = `${PATHS.LOGOUT}`;
            return Promise.reject(error);
          }

          error.config.headers["Authorization"] = `Bearer ${token}`;
          // Fire all the queued requests
          requestQueue.forEach((queuedRequest: any) => {
            queuedRequest.config.headers["Authorization"] = `Bearer ${token}`;
            client(queuedRequest.config)
              .then((res) => {
                // Resolve the queued request
                queuedRequest.resolve(res);
              })
              .catch((err) => {
                // Reject the queued request
                queuedRequest.reject(err);
              });
          });
          requestQueue.length = 0;
          isRefreshing = false;
          // Outside the try on purpose: the retried request failing (a 404, a
          // validation error) is that request's own error, not a dead session.
          // It used to share the refresh's catch and sign the admin out.
          return client(originalRequest);
        } else {
          // Queue the failed request
          const deferred: any = {};
          deferred.promise = new Promise((resolve, reject) => {
            deferred.resolve = resolve;
            deferred.reject = reject;
          });
          requestQueue.push({
            config: error.config,
            resolve: deferred.resolve,
            reject: deferred.reject,
          });
          return deferred.promise;
        }
      } else {
        // No refresh token, logout
        window.location.href = `${PATHS.LOGOUT}`;
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default client;
